import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import {
  selectors,
  updatePizzaFinisher
} from '../../../slice/pizza.slice';
import { debouncedUpdateFinishers } from '../../../slice/debouncedActions';
import { PizzaIngredient, PizzaIngredientOption } from '@/builders/pizza/dataTransformers/builderTypes';
import FinisherPicker from '../FinisherPicker/FinisherPicker';
import HeaderText from '@/common/HeaderText';

const FinishersSetterDesktop = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const [{ enabled: isMultiFinisherEnabled }] = useDecision('dtg-1723-dynamic-finishers-section');
  const finisherOptions = useSelector(selectors.selectFinisherOptions);
  const selectedFinishers = useSelector(selectors.selectPizzaFinishers);
  const maxAllowedFinishers = useSelector(selectors.selectMaxAllowedFinishers);
  const currentCrust = useSelector(selectors.selectPizzaCrustOption);

  const isExcludedFinisherFromVariant = (finisher: PizzaIngredientOption, variantCode: string | undefined) => {
    if (!finisher.excludedVariants || !variantCode) {
      return false;
    }
    return finisher.weights?.every((weightKey) => {
      const variants = finisher.excludedVariants?.[weightKey];
      return variants && variants.includes(variantCode);
    });
  };
  const filteredFinisherOptions = finisherOptions.filter(
    finisher => !isExcludedFinisherFromVariant(finisher, currentCrust?.variantCode)
  );

  const onFinishersUpdate = useCallback((finishers: PizzaIngredient[]) => {
    if (isMultiFinisherEnabled) {
      dispatch(debouncedUpdateFinishers(finishers));
    } else {
      const singleFinisher = finishers.length > 0 ? finishers[finishers.length - 1] : null;
      dispatch(updatePizzaFinisher(singleFinisher));
    }
  }, [dispatch, isMultiFinisherEnabled]);

  useEffect(() => {
    // Ensure that the selected finishers do not exceed the max allowed
    if (selectedFinishers.length > maxAllowedFinishers && isMultiFinisherEnabled) {
      const updatedFinishers = selectedFinishers.slice(0, maxAllowedFinishers);
      onFinishersUpdate(updatedFinishers);
    }
  }, [isMultiFinisherEnabled, maxAllowedFinishers, onFinishersUpdate, selectedFinishers]);

  return (
    filteredFinisherOptions.length ? <div  data-testid="finishers-setter-desktop">
      <HeaderText text="Finishers" />
      <FinisherPicker
        finisherOptions={filteredFinisherOptions}
        selectedFinishers={selectedFinishers}
        onFinishersUpdate={onFinishersUpdate}
        maxAllowedFinishers={isMultiFinisherEnabled ? maxAllowedFinishers : 1}
        multiSelectEnabled={isMultiFinisherEnabled}
      />
    </div> : null
  );
};

export default FinishersSetterDesktop;
